import { Info } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { SectionMessage } from '@/components/atoms/SectionMessage';
import { ROUTES } from '@/config/routes';
import { useGroundingQuality } from '@/services/Quality';
import { formatDate } from '@/utils/datetime';

import { DAY_SEGMENT_ORDER, SEGMENT_FILL_CLASS, SEGMENT_LABEL_KEY, WINDOW_DAYS } from './Quality.constants';
import { DailyReports } from './Quality.DailyReports';
import { QualityLoading } from './Quality.loading';
import { styles } from './Quality.styles';
import type { ByDayBarProps, ByDayLegendProps, DaySegmentTotals, KpiCardProps } from './Quality.types';
import { daySegments, formatRate, isEmptyWindow, maxDayChecks } from './Quality.utils';

const KpiCard = ({ label, value, sub }: KpiCardProps) => (
	<article className={styles.kpiCard}>
		<p className={styles.kpiLabel}>{label}</p>
		<p className={styles.kpiValue}>{value}</p>
		<p className={styles.kpiSub}>{sub}</p>
	</article>
);

const ByDayBar = ({ day, maxChecks }: ByDayBarProps) => {
	const { t, i18n } = useTranslation('translation', { keyPrefix: 'quality' });
	const widthPct = (day.checks / maxChecks) * 100;
	const rowLabel = t('BY_DAY_ROW_ARIA', {
		day: formatDate(day.day, i18n.language),
		checks: day.checks,
		clean: day.clean,
		scrubbed: day.scrubbed,
		errors: day.errors,
	});

	return (
		<div className={styles.dayRow}>
			<span className="sr-only">{rowLabel}</span>
			<span className={styles.dayLabel} aria-hidden="true">
				{formatDate(day.day, i18n.language)}
			</span>
			<div className={styles.dayTrack} aria-hidden="true">
				<div className="flex h-full" style={{ width: `${widthPct}%` }}>
					{daySegments(day).map(
						({ key, value }) =>
							value > 0 && (
								<div
									key={key}
									className={SEGMENT_FILL_CLASS[key]}
									style={{ width: `${(value / day.checks) * 100}%` }}
									title={`${t(SEGMENT_LABEL_KEY[key])}: ${value}`}
								/>
							),
					)}
				</div>
			</div>
			<span className={styles.dayTotal} aria-hidden="true">
				{day.checks}
			</span>
		</div>
	);
};

const ByDayLegend = ({ data }: ByDayLegendProps) => {
	const { t } = useTranslation('translation', { keyPrefix: 'quality' });
	const totals: DaySegmentTotals = {
		clean: data.clean,
		flagged: data.flagged,
		scrubbed: data.scrubbed,
		errors: data.errors,
	};

	return (
		<ul className={styles.legend} aria-label={t('BY_DAY_LEGEND_ARIA')}>
			{DAY_SEGMENT_ORDER.map((key) => (
				<li key={key} className={styles.legendItem}>
					<span className={`${styles.legendDot} ${SEGMENT_FILL_CLASS[key]}`} aria-hidden />
					{t(SEGMENT_LABEL_KEY[key])} · {totals[key]}
				</li>
			))}
		</ul>
	);
};

const LabCallout = () => {
	const { t } = useTranslation('translation', { keyPrefix: 'quality' });

	return (
		<aside className={styles.callout}>
			<Info className="mt-3xs h-md w-md shrink-0 text-purple-700" aria-hidden="true" />
			<p className={styles.calloutText}>
				<span className="font-semibold">{t('LAB_CALLOUT_TITLE')} </span>
				{t('LAB_CALLOUT_BODY')}{' '}
				<Link to={ROUTES.labEvaluations} className={styles.calloutLink}>
					{t('LAB_CALLOUT_LINK')}
				</Link>
			</p>
		</aside>
	);
};

const QualitySignals = ({ data }: ByDayLegendProps) => {
	const { t } = useTranslation('translation', { keyPrefix: 'quality' });
	const maxChecks = maxDayChecks(data.byDay);

	return (
		<>
			<section className={styles.kpiGrid} aria-label={t('TITLE')}>
				<KpiCard
					label={t('KPI_VERIFIED_LABEL')}
					value={formatRate(data.verifiedRate)}
					sub={t('KPI_VERIFIED_SUB', { clean: data.clean, checks: data.checks })}
				/>
				<KpiCard label={t('KPI_UNSUPPORTED_LABEL')} value={String(data.scrubbed)} sub={t('KPI_UNSUPPORTED_SUB')} />
				<KpiCard
					label={t('KPI_CONTRADICTIONS_LABEL')}
					value={String(data.contradictions)}
					sub={t('KPI_CONTRADICTIONS_SUB', { windowDays: data.windowDays })}
				/>
				<KpiCard label={t('KPI_ERRORS_LABEL')} value={String(data.errors)} sub={t('KPI_ERRORS_SUB')} />
			</section>

			<section className={styles.chartCard}>
				<h2 className={styles.chartTitle}>{t('BY_DAY_TITLE')}</h2>
				<div className="flex flex-col gap-2xs">
					{data.byDay.map((day) => (
						<ByDayBar key={day.day} day={day} maxChecks={maxChecks} />
					))}
				</div>
				<ByDayLegend data={data} />
			</section>
		</>
	);
};

export const Quality = () => {
	const { t } = useTranslation('translation', { keyPrefix: 'quality' });
	const { data, isLoading, isError } = useGroundingQuality(WINDOW_DAYS);

	if (isLoading) return <QualityLoading />;
	if (isError || !data) {
		return (
			<main className={styles.content}>
				<p className="text-[13px] text-error">{t('ERROR_LOAD')}</p>
			</main>
		);
	}

	return (
		<>
			<header className={styles.topbar}>
				<nav className="flex items-center gap-3xs text-[12px] text-subtle">
					<span className="font-medium text-default">{t('BREADCRUMB')}</span>
				</nav>
			</header>

			<main className={styles.content}>
				<header>
					<p className={styles.pageEyebrow}>{t('EYEBROW')}</p>
					<h1 className={styles.pageH1}>{t('TITLE')}</h1>
					<p className={styles.pageSub}>{t('SUBTITLE')}</p>
				</header>

				{isEmptyWindow(data) ? <SectionMessage>{t('EMPTY_BODY')}</SectionMessage> : <QualitySignals data={data} />}

				<section className={styles.reportsSection}>
					<h2 className={styles.sectionH}>{t('REPORTS_SECTION_TITLE')}</h2>
					<DailyReports />
				</section>

				<LabCallout />
			</main>
		</>
	);
};
